import {Component, Input, OnDestroy, OnInit, OnChanges, SimpleChanges} from '@angular/core';
import {AngularFirestore} from '@angular/fire/compat/firestore';
import {Subject} from 'rxjs';
import {map, takeUntil} from 'rxjs/operators';
import * as XLSX from 'xlsx';
import {LoadingService} from "../../../../services/loading.service";
import {PermissionType} from "../../../../components/employee/PermissionType.enum";
import {FinancialYearService} from "../../../../services/financial-year.service";
import {endOfDay} from '../../../../utils/date-range-format.service';

interface CentreData {
  centre: string;
  count: number;
}

interface TableRow {
  teacher: string;
  centres: CentreData[];
  total: number;
}

@Component({
  selector: 'app-education-count-report',
  templateUrl: './education-count-report.component.html',
  styleUrls: ['./education-count-report.component.scss']
})
export class EducationCountReportComponent implements OnInit, OnChanges, OnDestroy {
  private _destroyed$ = new Subject<void>();
  // Input properties from parent component
  @Input() hideFilters: boolean = false;
  @Input() externalStartDate: Date | null = null;
  @Input() externalEndDate: Date | null = null;
  @Input() externalCentre: string | string[] = '';
  @Input() triggerSearch: number = 0;

  startDate!: Date;
  endDate!: Date;
  selectedYear: number = new Date().getFullYear() - 1;
  selectedCentre: string = 'All Centres';
  /** Normalised list of selected centre codes (empty = all) */
  selectedCentreCodes: string[] = [];
  selectedTeacher: string = 'All Teachers';
  uniqueTeachers: string[] = [];
  uniqueCentres: string[] = [];
  groupUniqueCentres: string[] = [];
  tableData: TableRow[] = [];
  ageAboveChildData: TableRow[] = [];
  ageBelowChildData: TableRow[] = [];
  groupTableData: TableRow[] = [];
  totalIndividualSessions: number[] = [];
  totalGroupSessions: number[] = [];
  grandTotalIndividualSessions: number = 0;
  grandTotalGroupSessions: number = 0;
  fileName = 'Education_Count_Report.xlsx';
  fileNameVolunteer = 'Volunteer_Count_Report.xlsx';
  fileNameAboveAge = 'Above_Age_Count_Report.xlsx';
  fileNameBelowAge = 'Below_Age_Count_Report.xlsx';
  volunteerTableData: TableRow[] = [];
  volunteerGroupTableData: TableRow[] = [];
  totalIndividualVolunteerSessions: number[] = [];
  totalGroupVolunteerSessions: number[] = [];
  grandTotalIndividualVolunteerSessions: number = 0;
  grandTotalGroupVolunteerSessions: number = 0;
  volunteerCombinedTableData: TableRow[] = [];
  totalCombinedVolunteerSessions: number[] = [];
  grandTotalCombinedVolunteerSessions: number = 0;
  volunteerUniqueCentres: string[] = [];
  grandTotalIndividualAboveAgeSessions: number = 0;
  totalIndividualAboveAgeSessions: number[] = [];
  grandTotalIndividualBelowAgeSessions: number = 0;
  totalIndividualBelowAgeSessions: number[] = [];



  constructor(private readonly mFirestore: AngularFirestore,
              private LoadingService: LoadingService,
              private readonly financialYearService: FinancialYearService) {
  }

  ngOnInit(): void {
    // Only default here in standalone mode — nested mode (hideFilters=true) is fully
    // driven by externalStartDate/externalEndDate via ngOnChanges. @Input bindings are
    // already applied by the time ngOnInit runs, so hideFilters reflects the real value here.
    if (!this.hideFilters) {
      const fy = this.financialYearService.getCurrentFinancialYear();
      this.startDate = fy.startDate;
      this.endDate = fy.endDate;
      this.selectedYear = fy.startDate.getFullYear();
    } else {
      this.setFinancialYear(this.selectedYear);
    }
    this.loadUniqueValues();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Normalise to a clean string[] of centre codes
    const normaliseCentres = (c: string | string[]): string[] => {
      const arr = Array.isArray(c) ? c : (c ? [c] : []);
      return arr.filter(code => code && code !== 'All Centres' && code !== 'All');
    };

    // Sync external inputs from parent component (always, regardless of hideFilters)
    if (changes['externalStartDate'] && this.externalStartDate) {
      this.startDate = this.externalStartDate;
    }
    if (changes['externalEndDate'] && this.externalEndDate) {
      this.endDate = this.externalEndDate;
    }
    if (changes['externalCentre'] && this.externalCentre) {
      this.selectedCentreCodes = normaliseCentres(this.externalCentre);
      this.selectedCentre = this.selectedCentreCodes.length === 1
        ? this.selectedCentreCodes[0] : 'All Centres';
    }

    // Auto-trigger search when external params change
    if ((changes['externalStartDate'] || changes['externalEndDate'] || changes['externalCentre'])
        && this.startDate && this.endDate) {
      setTimeout(() => this.search(), 100);
    }

    // Explicit search trigger from parent
    if (changes['triggerSearch'] && changes['triggerSearch'].currentValue > 0) {
      if (this.externalStartDate && this.externalEndDate) {
        this.startDate = this.externalStartDate;
        this.endDate = this.externalEndDate;
        this.selectedCentreCodes = normaliseCentres(this.externalCentre);
        this.selectedCentre = this.selectedCentreCodes.length === 1
          ? this.selectedCentreCodes[0] : 'All Centres';
      }
      setTimeout(() => this.search(), 100);
    }
  }

  // Function to set the financial year
  setFinancialYear(year: number) {
    this.startDate = new Date(year, 3, 1); // April 1st of the selected year
    this.endDate = new Date(year + 1, 2, 31); // March 31st of the next year
    // Force change detection by wrapping in setTimeout
    setTimeout(() => {
    }, 0);
  }

  // Function to handle year change from UI
  onYearChange(event: any) {
    debugger
    this.selectedYear = event.value;
    this.setFinancialYear(this.selectedYear);
  }

  loadUniqueValues() {
    // Only load uniqueTeachers here — uniqueCentres and groupUniqueCentres are
    // derived per-search from actual returned data so they respect the active centre filter.
    this.mFirestore.collection<any>('educationTest').valueChanges().pipe(
      map(data => {
        this.uniqueTeachers = Array.from(new Set(data.map(item => item.submittedBy).filter(item => item))).sort();
      }),
      takeUntil(this._destroyed$)
    ).subscribe();
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }


  // search() {
  //   this.LoadingService.setLoading(true);
  //   const startDate = this.startDate ? this.startDate : new Date(this.selectedYear, 3, 1); // April 1st
  //   const endDate = this.endDate ? this.endDate : new Date(this.selectedYear + 1, 2, 31); // March 31st
  //   this.mFirestore.collection<any>('educationTest', ref => {
  //     // @ts-ignore
  //     let query: firebase.firestore.CollectionReference | firebase.firestore.Query = ref;
  //     query = query.where('sessionDate', '>=', startDate).where('sessionDate', '<=', endOfDay(endDate)).where('present', '==', 'Present');
  //     if (this.selectedCentre !== 'All Centres') {
  //       query = query.where('centreName', '==', this.selectedCentre);
  //     }
  //     if (this.selectedTeacher !== 'All Teachers') {
  //       query = query.where('submittedBy', '==', this.selectedTeacher);
  //     }
  //     return query;
  //   }).valueChanges().pipe(
  //     map(data => this.processData(data))
  //   ).subscribe(tableData => {
  //     this.tableData = tableData;
  //     this.calculateTotals();
  //     this.LoadingService.setLoading(false);
  //   });
  //
  //   // Group session query
  //   this.mFirestore.collection<any>('educationGroupTest', ref => {
  //     // @ts-ignore
  //     let query: firebase.firestore.CollectionReference | firebase.firestore.Query = ref;
  //     query = query.where('sessionDate', '>=', startDate).where('sessionDate', '<=', endOfDay(endDate));
  //     if (this.selectedCentre !== 'All Centres') {
  //       query = query.where('centres', 'array-contains', {centreName: this.selectedCentre});
  //     }
  //     if (this.selectedTeacher !== 'All Teachers') {
  //       query = query.where('submittedBy', '==', this.selectedTeacher);
  //     }
  //     return query;
  //   }).valueChanges().pipe(
  //     map(data => this.processGroupData(data))
  //   ).subscribe(groupTableData => {
  //     this.groupTableData = groupTableData;
  //     this.calculateGroupTotals();
  //
  //     this.calculateVolunteerSessions(startDate, endDate);
  //
  //     this.LoadingService.setLoading(false);
  //   });
  // }

  search() {
    this.LoadingService.setLoading(true);
    const startDate = this.startDate ? this.startDate : new Date(this.selectedYear, 3, 1);
    const endDate = this.endDate ? this.endDate : new Date(this.selectedYear + 1, 2, 31);

    // Reset combined table while loading
    this.volunteerCombinedTableData = [];

    this.mFirestore.collection<any>('educationTest', ref => {
      let query: any = ref;
      query = query.where('sessionDate', '>=', startDate).where('sessionDate', '<=', endOfDay(endDate)).where('present', '==', 'Present');
      // Filter by centreCode (not centreName) — selectedCentreCodes holds codes, not display names
      if (this.selectedCentreCodes.length === 1) {
        query = query.where('centreCode', '==', this.selectedCentreCodes[0]);
      }
      if (this.selectedTeacher !== 'All Teachers') query = query.where('submittedBy', '==', this.selectedTeacher);
      return query;
    }).valueChanges().pipe(
      map((data: any[]) => {
        // Post-filter for multiple selected centres
        if (this.selectedCentreCodes.length > 1) {
          data = data.filter((x: any) => this.selectedCentreCodes.includes(x.centreCode));
        }
        // Derive column headers from actual returned data so only relevant centres show
        this.uniqueCentres = Array.from(new Set(data.map((x: any) => x.centreName).filter(Boolean))).sort();
        this.tableData = this.processData(data);
        this.ageAboveChildData = this.ageAboveData(data);
        this.ageBelowChildData = this.ageBelowData(data);
        this.volunteerTableData = this.processVolunteerData(data);
        this.calculateTotals();
        this.calculateAboveAgeTotals();
        this.calculateBelowAgeTotals();
        this.calculateVolunteerTotals();
        this.buildVolunteerCombinedTable();
        this.LoadingService.setLoading(false);
      }),
      takeUntil(this._destroyed$)
    ).subscribe();

    this.mFirestore.collection<any>('educationGroupTest', ref => {
      let query: any = ref;
      query = query.where('sessionDate', '>=', startDate).where('sessionDate', '<=', endOfDay(endDate));
      // array-contains requires an exact object match, so centreCode alone won't work.
      // Fetch all sessions for the date range and post-filter by centreCode instead.
      if (this.selectedTeacher !== 'All Teachers') query = query.where('submittedBy', '==', this.selectedTeacher);
      return query;
    }).valueChanges().pipe(
      map((data: any[]) => {
        // Filter by selected centres (handles single and multiple)
        if (this.selectedCentreCodes.length > 0) {
          data = data.filter((x: any) =>
            x.centres?.some((c: any) => this.selectedCentreCodes.includes(c.centreCode))
          );
        }
        // Derive group column headers from actual returned data
        this.groupUniqueCentres = Array.from(
          new Set(data.flatMap((x: any) => (x.centres || []).map((c: any) => c.centreName)).filter(Boolean))
        ).sort();
        this.groupTableData = this.processGroupData(data);
        this.volunteerGroupTableData = this.processVolunteerGroupData(data);
        this.calculateGroupTotals();
        this.calculateVolunteerGroupTotals();
        this.buildVolunteerCombinedTable();
        this.LoadingService.setLoading(false);
      }),
      takeUntil(this._destroyed$)
    ).subscribe();
  }

  private processData(data: any[]): TableRow[] {
    const tableData: TableRow[] = [];
    data = data.filter((x: any) => x.volunteerName == '')
    const groupedByTeacher = data.reduce((acc: { [key: string]: any[] }, curr) => {
      acc[curr.submittedBy] = acc[curr.submittedBy] || [];
      acc[curr.submittedBy].push(curr);
      return acc;
    }, {});

    for (const teacher in groupedByTeacher) {
      const centresData: CentreData[] = [];
      let total = 0;

      for (const centre of this.uniqueCentres) {
        const count = groupedByTeacher[teacher].filter(item => item.centreName === centre).length;
        centresData.push({centre, count});
        total += count;
      }

      tableData.push({teacher, centres: centresData, total});
    }

    return tableData.sort((a, b) => a.teacher.localeCompare(b.teacher));
  }


  private ageAboveData(data: any[]): TableRow[] {
    const tableData: TableRow[] = [];
    data = data.filter((x: any) => x.age >= 14)
    console.log("age Data",  data)
    const groupedByTeacher = data.reduce((acc: { [key: string]: any[] }, curr) => {
      acc[curr.submittedBy] = acc[curr.submittedBy] || [];
      acc[curr.submittedBy].push(curr);
      return acc;
    }, {});

    for (const teacher in groupedByTeacher) {
      const centresData: CentreData[] = [];
      let total = 0;

      for (const centre of this.uniqueCentres) {
        const count = groupedByTeacher[teacher].filter(item => item.centreName === centre).length;
        centresData.push({centre, count});
        total += count;
      }

      tableData.push({teacher, centres: centresData, total});
    }

    return tableData.sort((a, b) => a.teacher.localeCompare(b.teacher));
  }

  private ageBelowData(data: any[]): TableRow[] {
    const tableData: TableRow[] = [];
    data = data.filter((x: any) => x.age < 14)
    console.log("age below Data",  data)
    const groupedByTeacher = data.reduce((acc: { [key: string]: any[] }, curr) => {
      acc[curr.submittedBy] = acc[curr.submittedBy] || [];
      acc[curr.submittedBy].push(curr);
      return acc;
    }, {});

    for (const teacher in groupedByTeacher) {
      const centresData: CentreData[] = [];
      let total = 0;

      for (const centre of this.uniqueCentres) {
        const count = groupedByTeacher[teacher].filter(item => item.centreName === centre).length;
        centresData.push({centre, count});
        total += count;
      }

      tableData.push({teacher, centres: centresData, total});
    }

    return tableData.sort((a, b) => a.teacher.localeCompare(b.teacher));
  }

  private processGroupData(data: any[]): TableRow[] {
    const tableData: TableRow[] = [];
    data = data.filter((x: any) => x.volunteerName == '')

    const groupedByTeacher = data.reduce((acc: { [key: string]: any[] }, curr) => {
      acc[curr.submittedBy] = acc[curr.submittedBy] || [];
      acc[curr.submittedBy].push(curr);
      return acc;
    }, {});

    for (const teacher in groupedByTeacher) {
      const centresData: CentreData[] = [];
      let total = 0;

      for (const centre of this.groupUniqueCentres) {
        const count = groupedByTeacher[teacher].filter(item => item.centres.some((c: any) => c.centreName === centre)).length;
        centresData.push({centre, count});
        total += count;
      }

      tableData.push({teacher, centres: centresData, total});
    }

    return tableData.sort((a, b) => a.teacher.localeCompare(b.teacher));
  }

  private calculateTotals() {
    this.totalIndividualSessions = this.uniqueCentres.map((_, index) =>
      this.tableData.reduce((sum, row) => sum + (row.centres[index]?.count || 0), 0)
    );
    this.grandTotalIndividualSessions = this.tableData.reduce((sum, row) => sum + row.total, 0);
  }

  private calculateGroupTotals() {
    this.totalGroupSessions = this.groupUniqueCentres.map((_, index) =>
      this.groupTableData.reduce((sum, row) => sum + (row.centres[index]?.count || 0), 0)
    );
    this.grandTotalGroupSessions = this.groupTableData.reduce((sum, row) => sum + row.total, 0);
  }

  private calculateAboveAgeTotals() {
    this.totalIndividualAboveAgeSessions = this.uniqueCentres.map((_, index) =>
      this.ageAboveChildData.reduce((sum, row) => sum + (row.centres[index]?.count || 0), 0)
    );
    this.grandTotalIndividualAboveAgeSessions = this.ageAboveChildData.reduce((sum, row) => sum + row.total, 0);
  }

  private calculateBelowAgeTotals() {
    this.totalIndividualBelowAgeSessions = this.uniqueCentres.map((_, index) =>
      this.ageBelowChildData.reduce((sum, row) => sum + (row.centres[index]?.count || 0), 0)
    );
    this.grandTotalIndividualBelowAgeSessions = this.ageBelowChildData.reduce((sum, row) => sum + row.total, 0);
  }

  getCentreCount(data: TableRow, centre: string): number {
    return data.centres.find(c => c.centre === centre)?.count || 0;
  }

  private processVolunteerData(data: any[]): TableRow[] {
    const tableData: TableRow[] = [];
    // Filter where volunteerName is NOT empty
    data = data.filter((x: any) => x.volunteerName && x.volunteerName !== '');

    const groupedByVolunteer = data.reduce((acc: { [key: string]: any[] }, curr) => {
      const key = curr.volunteerName;
      acc[key] = acc[key] || [];
      acc[key].push(curr);
      return acc;
    }, {});

    for (const volunteer in groupedByVolunteer) {
      const centresData: CentreData[] = [];
      let total = 0;

      for (const centre of this.uniqueCentres) {
        const count = groupedByVolunteer[volunteer].filter(item => item.centreName === centre).length;
        centresData.push({ centre, count });
        total += count;
      }

      tableData.push({ teacher: volunteer, centres: centresData, total });
    }

    return tableData.sort((a, b) => a.teacher.localeCompare(b.teacher));
  }

  private processVolunteerGroupData(data: any[]): TableRow[] {
    const tableData: TableRow[] = [];
    data = data.filter((x: any) => x.volunteerName && x.volunteerName !== '');
    console.log("Filtered Data:", data);

    const groupedByVolunteer = data.reduce((acc: { [key: string]: any[] }, curr) => {
      const key = curr.volunteerName;
      acc[key] = acc[key] || [];
      acc[key].push(curr);
      return acc;
    }, {});

    for (const volunteer in groupedByVolunteer) {
      const centresData: CentreData[] = [];
      let total = 0;

      for (const centre of this.groupUniqueCentres) {
        // Filter sessions belonging to this centre
        const centreItems = groupedByVolunteer[volunteer]
          .filter(item => item.centres && item.centres.some((c: any) => c.centreName === centre));

        // Calculate count based on countType
        const count = centreItems.reduce((sum: number, item: any) => {
          if (item.countType === 'bychildren') {
            // Count number of present children in the children array
            const presentChildren = (item.children || [])
              .filter((child: any) => child.status === 'Present').length;
            return sum + presentChildren;
          } else {
            // countType === 'bysession' or anything else → count as 1
            return sum + 1;
          }
        }, 0);

        centresData.push({ centre, count });
        total += count;
      }

      tableData.push({ teacher: volunteer, centres: centresData, total });
    }

    return tableData.sort((a, b) => a.teacher.localeCompare(b.teacher));
  }

  private calculateVolunteerTotals() {
    this.totalIndividualVolunteerSessions = this.uniqueCentres.map((_, index) =>
      this.volunteerTableData.reduce((sum, row) => sum + (row.centres[index]?.count || 0), 0)
    );
    this.grandTotalIndividualVolunteerSessions = this.volunteerTableData.reduce((sum, row) => sum + row.total, 0);
  }

  private calculateVolunteerGroupTotals() {
    this.totalGroupVolunteerSessions = this.groupUniqueCentres.map((_, index) =>
      this.volunteerGroupTableData.reduce((sum, row) => sum + (row.centres[index]?.count || 0), 0)
    );
    this.grandTotalGroupVolunteerSessions = this.volunteerGroupTableData.reduce((sum, row) => sum + row.total, 0);
  }

  // exportexcel() {
  //   let element = document.getElementById('excel-table');
  //   const ws: XLSX.WorkSheet = XLSX.utils.table_to_sheet(element);
  //   const wb: XLSX.WorkBook = XLSX.utils.book_new();
  //   XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
  //   XLSX.writeFile(wb, this.fileName);
  // }
  exportexcel() {
    let element = document.getElementById('excel-table');
    const ws: XLSX.WorkSheet = XLSX.utils.table_to_sheet(element);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    const title = [
      [`Education Report - Individual Sessions Count (${this.formatDate(this.startDate)} to ${this.formatDate(this.endDate)})`]
    ];
    XLSX.utils.sheet_add_aoa(ws, title, { origin: 'A1' });
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, this.fileName);
  }


  // exportGroupExcel() {
  //   let element = document.getElementById('group-excel-table');
  //   const ws: XLSX.WorkSheet = XLSX.utils.table_to_sheet(element);
  //   const wb: XLSX.WorkBook = XLSX.utils.book_new();
  //   XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
  //   XLSX.writeFile(wb, this.fileName);
  // }
  exportGroupExcel() {
    let element = document.getElementById('group-excel-table');
    const ws: XLSX.WorkSheet = XLSX.utils.table_to_sheet(element);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    const title = [
      [`Education Report - Group Sessions Count (${this.formatDate(this.startDate)} to ${this.formatDate(this.endDate)})`]
    ];
    XLSX.utils.sheet_add_aoa(ws, title, { origin: 'A1' });
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, this.fileName);
  }

  exportVolunteerSessionExcel() {
    let element = document.getElementById('volunteer-combined-table');
    const ws: XLSX.WorkSheet = XLSX.utils.table_to_sheet(element);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    const title = [
      [`Education Report - Volunteer Sessions Count (${this.formatDate(this.startDate)} to ${this.formatDate(this.endDate)})`]
    ];
    XLSX.utils.sheet_add_aoa(ws, title, { origin: 'A1' });
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, this.fileNameVolunteer);
  }

  exportAboveAgeSessionExcel() {
    let element = document.getElementById('excel-above-age-table');
    const ws: XLSX.WorkSheet = XLSX.utils.table_to_sheet(element);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    const title = [
      [`Education Report - Individual Above Age Sessions Count (${this.formatDate(this.startDate)} to ${this.formatDate(this.endDate)})`]
    ];
    XLSX.utils.sheet_add_aoa(ws, title, { origin: 'A1' });
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, this.fileNameAboveAge);
  }

  exportBelowAgeSessionExcel() {
    let element = document.getElementById('excel-below-age-table');
    const ws: XLSX.WorkSheet = XLSX.utils.table_to_sheet(element);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    const title = [
      [`Education Report - Individual Below Age Sessions Count (${this.formatDate(this.startDate)} to ${this.formatDate(this.endDate)})`]
    ];
    XLSX.utils.sheet_add_aoa(ws, title, { origin: 'A1' });
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, this.fileNameBelowAge);
  }

// Helper function to format the date (DD/MM/YYYY)
  formatDate(date: Date | null): string {
    if (!date) return 'N/A';
    const d = new Date(date);
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
  }

  ClearAll() {
    const fy = this.financialYearService.getCurrentFinancialYear();
    this.startDate = fy.startDate;
    this.endDate = fy.endDate;
    this.selectedYear = fy.startDate.getFullYear();
    this.selectedCentre = null as any;
    this.selectedTeacher = null as any;

    // Clear table data
    this.tableData = [];
    this.groupTableData = [];

    // Reset totals
    this.totalIndividualSessions = [];
    this.grandTotalIndividualSessions = 0;
    this.totalGroupSessions = [];
    this.grandTotalGroupSessions = 0;

    this.volunteerTableData = [];
    this.volunteerGroupTableData = [];
    this.volunteerCombinedTableData = [];
    this.totalIndividualVolunteerSessions = [];
    this.grandTotalIndividualVolunteerSessions = 0;
    this.totalGroupVolunteerSessions = [];
    this.grandTotalGroupVolunteerSessions = 0;
    this.totalCombinedVolunteerSessions = [];
    this.grandTotalCombinedVolunteerSessions = 0;
    this.ageAboveChildData = [];
    this.ageBelowChildData = [];
    this.totalIndividualAboveAgeSessions = [];
    this.grandTotalIndividualAboveAgeSessions = 0;
    this.totalIndividualBelowAgeSessions = [];
    this.grandTotalIndividualBelowAgeSessions = 0;
  }

  private buildVolunteerCombinedTable() {
    // Merge uniqueCentres + groupUniqueCentres into one deduplicated sorted list
    this.volunteerUniqueCentres = Array.from(
      new Set([...this.uniqueCentres, ...this.groupUniqueCentres])
    ).sort();

    // Collect all volunteer names from both tables
    const allVolunteers = Array.from(
      new Set([
        ...this.volunteerTableData.map(r => r.teacher),
        ...this.volunteerGroupTableData.map(r => r.teacher)
      ])
    ).sort();

    this.volunteerCombinedTableData = allVolunteers.map(volunteer => {
      const centresData: CentreData[] = [];
      let total = 0;

      for (const centre of this.volunteerUniqueCentres) {
        // Get count from individual volunteer table
        const individualRow = this.volunteerTableData.find(r => r.teacher === volunteer);
        const individualCount = individualRow?.centres.find(c => c.centre === centre)?.count || 0;

        // Get count from volunteer group table
        const groupRow = this.volunteerGroupTableData.find(r => r.teacher === volunteer);
        const groupCount = groupRow?.centres.find(c => c.centre === centre)?.count || 0;

        const combinedCount = individualCount + groupCount;
        centresData.push({ centre, count: combinedCount });
        total += combinedCount;
      }

      return { teacher: volunteer, centres: centresData, total };
    });

    this.calculateCombinedVolunteerTotals();
  }

  private calculateCombinedVolunteerTotals() {
    this.totalCombinedVolunteerSessions = this.volunteerUniqueCentres.map((_, index) =>
      this.volunteerCombinedTableData.reduce((sum, row) => sum + (row.centres[index]?.count || 0), 0)
    );
    this.grandTotalCombinedVolunteerSessions = this.volunteerCombinedTableData
      .reduce((sum, row) => sum + row.total, 0);
  }

  // Expose PermissionType to template
  readonly PermissionType = PermissionType;

}

